import {
  PartnerDealBriefingFinalReviewResponse,
  PartnerDealBriefingGenerationCostResponse,
  PartnerDealBriefingReadinessStatus,
  PartnerDealBriefingSectionResponse,
  PartnerDealBriefingVersionResponse,
} from '@interfaces/partners/responses/partner-deal-briefing.response';
import { BRIEFING_PROPOSED_STRUCTURE_KEY, applyShortcut } from './briefing-sections';

/**
 * Textos e mapeamentos de exibição da aba Briefing (card 3878). Só formatação: quem decide prontidão, seções e
 * estado de cada versão é o backend.
 */

/** O que cada seção traz, para a pessoa escolher sem abrir o PDF. Seção nova no catálogo sem texto aqui fica sem descrição. */
const SECTION_DESCRIPTIONS: Record<string, string> = {
  header: 'Título da operação, tomadora, negócio, origem e data da versão.',
  recommendation: 'Avançar, aguardar ou declinar, com as condições e o parágrafo-síntese.',
  highlights: 'De 3 a 5 indicadores escolhidos pelo agente, com comentário.',
  pillars: 'Os três argumentos centrais do deal.',
  [BRIEFING_PROPOSED_STRUCTURE_KEY]:
    'Volume, modalidade, custo-alvo, hedge, garantias, covenants, bancos-alvo e fee.',
  indicators: 'Tabela por período, com a nota da fórmula do EBITDA.',
  red_flags: 'Achados da auditoria por nível. Crítico e material sempre entram.',
  next_steps: 'Passos, documentos-chave e responsável.',
};

/** Texto da seção: a descrição, ou o motivo de estar travada quando está desligada. */
export function sectionDescription(section: PartnerDealBriefingSectionResponse): string {
  if (!section.enabled) {
    return section.key === BRIEFING_PROPOSED_STRUCTURE_KEY
      ? 'Chega quando modalidade, custo-alvo, garantias e fee tiverem fonte definida.'
      : 'Indisponível por enquanto.';
  }
  return SECTION_DESCRIPTIONS[section.key] ?? '';
}

/** Tom da faixa de prontidão. */
export type BriefingReadinessTone = 'ready' | 'reading' | 'blocked';

export function readinessTone(status: PartnerDealBriefingReadinessStatus): BriefingReadinessTone {
  if (status === 'Blocked') return 'blocked';
  if (status === 'ReadyWithDocumentReading') return 'reading';
  return 'ready';
}

export const READINESS_TITLES: Record<BriefingReadinessTone, string> = {
  ready: 'Pronto para gerar',
  reading: 'Pronto, com leitura de documento',
  blocked: 'Ainda não dá para gerar',
};

/**
 * Corpo da caixa de prontidão (G1 da crítica de design): a mensagem do backend sem repetir o título que a tela já
 * mostra. Tolera as duas formas do backend, com o prefixo ("Pronto para gerar: o exercício...") e sem ele; o resto
 * começa com maiúscula.
 */
export function readinessBody(title: string, message: string | null | undefined): string {
  const text = (message ?? '').trim();
  const prefix = title.trim().toLowerCase();
  if (!prefix || !text.toLowerCase().startsWith(prefix)) return text;
  const rest = text
    .slice(prefix.length)
    .replace(/^[\s:.,;–-]+/, '')
    .trim();
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : text;
}

/** Motivo padrão quando a falha não tem texto ou o texto é de bastidor. */
export const GENERIC_FAILURE_REASON = 'A geração não terminou. Tente de novo.';

/**
 * Motivo da falha em linguagem de operação (N3 da crítica): mensagem do backend que fala de "fila" (bastidor) vira
 * "A geração não terminou. Tente de novo."; as demais passam como vieram.
 */
export function failureReasonText(message: string | null | undefined): string {
  const text = (message ?? '').trim();
  if (!text || /\bfila\b/i.test(text)) return GENERIC_FAILURE_REASON;
  return text;
}

/** A mensagem já avisa que a geração demora mais (o aviso extra da tela não se repete). */
export function mentionsSlowGeneration(message: string | null | undefined): boolean {
  return /demora mais/i.test(message ?? '');
}

/** Fases que a tela mostra enquanto a versão gera, na ordem do `LastPhase` do backend. */
export const GENERATION_PHASES: readonly string[] = [
  'Na fila',
  'Lendo os números',
  'Escrevendo',
  'Ajustando as páginas',
  // Revisão final do Cog (Q13): `lastPhase = "final_review"`, antes de a versão ficar pronta.
  'Conferindo o briefing',
];

/**
 * Índice da fase atual em {@link GENERATION_PHASES}, pelas seis fases do `lastPhase`: `loading_figures` (backend,
 * montando a base) e `reading` são a leitura; `writing`; `adjusting_pages` e `delivering` (Cog, entregando o
 * documento, antes da revisão final) ficam em "Ajustando as páginas"; `final_review`. Fase desconhecida conta como
 * leitura.
 */
export function generationPhaseIndex(version: PartnerDealBriefingVersionResponse): number {
  if (version.status === 'Queued') return 0;
  switch (version.lastPhase) {
    case 'writing':
      return 2;
    case 'adjusting_pages':
    case 'delivering':
      return 3;
    case 'final_review':
      return 4;
    default:
      return 1;
  }
}

/** Nome de cada etapa do custo da geração, quando o backend não manda o rótulo. */
const GENERATION_COST_STAGES: Readonly<Record<string, string>> = {
  briefing: 'Briefing',
  final_review: 'Revisão final',
  finalReview: 'Revisão final',
  document_reader: 'Leitura de documentos',
  documentReader: 'Leitura de documentos',
  investigation: 'Investigação da empresa',
};

/** Tokens em "12,3 mil"/"1,2 mi"; abaixo de mil, o número. */
function formatTokens(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  }
  return value.toLocaleString('pt-BR');
}

/** "12,3 mil tokens de entrada · 2,1 mil de saída"; nulo sem número. */
function tokensText(
  input: number | null | undefined,
  output: number | null | undefined
): string | null {
  const parts: string[] = [];
  if (input && input > 0) parts.push(`${formatTokens(input)} tokens de entrada`);
  if (output && output > 0) parts.push(`${formatTokens(output)} de saída`);
  return parts.length ? parts.join(' · ') : null;
}

/** Linha do popover de custo: etapa, custo e, se vierem, modelo e tokens. */
export interface GenerationCostRow {
  label: string;
  cost: string;
  model: string | null;
  tokens: string | null;
}

/** "US$ 0,19"; abaixo de 1 centavo, com 3 casas ("US$ 0,004") para não virar "US$ 0,00". */
export function formatUsd(value: number): string {
  const digits = value > 0 && value < 0.01 ? 3 : 2;
  return `US$ ${value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

/**
 * Custo de IA da geração para o popover do "$" (só ADMIN, quem decide o papel é o template): o total, uma linha por
 * etapa (briefing, revisão final, leitura de documentos, investigação) e, se vierem, modelo e tokens. Nulo sem o
 * campo.
 */
export function generationCostView(
  cost: PartnerDealBriefingGenerationCostResponse | null | undefined
): {
  total: string;
  /** Total em reais ("R$ 1,02"), só quando o backend manda (`totalBrl`, todas as linhas com PTAX). */
  totalBrl: string | null;
  rows: GenerationCostRow[];
} | null {
  if (!cost || typeof cost.totalUsd !== 'number' || !Number.isFinite(cost.totalUsd)) return null;
  const rows = (cost.items ?? [])
    .filter(item => Number.isFinite(item.usd))
    .map(item => ({
      label: item.label || GENERATION_COST_STAGES[item.step] || item.step,
      cost: formatUsd(item.usd),
      model: item.model?.trim() || null,
      tokens: tokensText(item.inputTokens, item.outputTokens),
    }));
  const brl = cost.totalBrl;
  return {
    total: formatUsd(cost.totalUsd),
    totalBrl:
      typeof brl === 'number' && Number.isFinite(brl)
        ? brl.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : null,
    rows,
  };
}

/** Resumo da revisão final do Cog numa versão pronta (Q13); nulo quando não houve ou não terminou. */
export function finalReviewSummary(
  review: PartnerDealBriefingFinalReviewResponse | null | undefined
): string | null {
  if (!review) return null;
  if (review.status === 'no-changes') return 'Conferido pelo Cog IA: sem ajustes';
  if (review.status !== 'adjusted') return null;
  const count = review.appliedEdits ?? review.items?.filter(item => item.applied).length ?? 0;
  if (count <= 0) return 'Conferido pelo Cog IA: sem ajustes';
  return `Conferido pelo Cog IA: ${count === 1 ? '1 ajuste' : `${count} ajustes`}`;
}

/** Versão na fila ou gerando: só existe uma por deal. */
export function isActiveVersion(version: PartnerDealBriefingVersionResponse): boolean {
  return version.status === 'Queued' || version.status === 'Generating';
}

/**
 * Selo da versão na lista, um só por card: na fila ou gerando, a fase atual ("Na fila", "Lendo os números",
 * "Escrevendo", "Ajustando as páginas"); "falhou"; "atual". Versão pronta que não é a atual não tem selo.
 */
export function versionTag(
  version: PartnerDealBriefingVersionResponse
): { label: string; tone: 'success' | 'info' | 'danger' } | null {
  switch (version.status) {
    case 'Queued':
    case 'Generating':
      return { label: GENERATION_PHASES[generationPhaseIndex(version)], tone: 'info' };
    case 'Failed':
      return { label: 'falhou', tone: 'danger' };
    default:
      return version.isCurrent ? { label: 'atual', tone: 'success' } : null;
  }
}

/**
 * Linha da versão (A8 da crítica): "Formato Resumido · 7 seções", "Formato Personalizado · 5 seções" ou "Ajuste pelo
 * chat: <pedido>". `separator` liga a origem ao detalhe.
 */
export function versionSummary(
  version: PartnerDealBriefingVersionResponse,
  catalog: readonly PartnerDealBriefingSectionResponse[]
): { origin: string; separator: string; detail: string } {
  if (version.trigger === 'ChatRevision') {
    return {
      origin: 'Ajuste pelo chat',
      separator: ': ',
      detail: version.requestText?.trim() || 'sem texto',
    };
  }
  // Card 3911: versão nova que leva a investigação ao texto (ajuste automático ou "Completar investigação").
  if (version.trigger === 'Investigation') {
    return { origin: 'Ajuste: investigação', separator: ' · ', detail: 'Red flags e Recomendação' };
  }

  const all = applyShortcut(catalog, 'summary');
  const sections = version.selectedSections ?? [];
  const isShortcut = sections.length === all.length && all.every(key => sections.includes(key));
  return {
    origin: isShortcut ? 'Formato Resumido' : 'Formato Personalizado',
    separator: ' · ',
    detail: sections.length === 1 ? '1 seção' : `${sections.length} seções`,
  };
}

/**
 * Caracteres que cabem em 2 linhas do card de versão (coluna de ~300px, texto de 13px). Acima disso o card corta em 2
 * linhas e oferece "ver mais". É uma régua aproximada: o corte visual é do CSS (`line-clamp`).
 */
export const REQUEST_TEXT_CLAMP_CHARS = 90;

/** O texto do pedido passa de 2 linhas no card de versão. */
export function isLongRequestText(text: string | null | undefined): boolean {
  return (text ?? '').trim().length > REQUEST_TEXT_CLAMP_CHARS;
}

/** Versões da mais nova para a mais antiga. */
export function sortVersions(
  versions: readonly PartnerDealBriefingVersionResponse[]
): PartnerDealBriefingVersionResponse[] {
  return [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
}

/** Nome do arquivo do PDF: `briefing-<número do deal>-v<versão>.pdf`. */
export function briefingPdfFileName(
  dealSequentialId: number | null,
  versionNumber: number
): string {
  return `briefing-${dealSequentialId ?? 'deal'}-v${versionNumber}.pdf`;
}

/** "1 documento" / "3 documentos". */
export function documentCountLabel(count: number): string {
  return count === 1 ? '1 documento' : `${count} documentos`;
}

/** Fase da investigação que o tempo real manda (`company-investigation.progress`, card 3911), em linguagem de tela. */
const INVESTIGATION_PHASE_LABELS: Readonly<Record<string, string>> = {
  sweep: 'Varrendo a internet',
  deepening: 'Aprofundando os achados',
  concluding: 'Fechando a conclusão',
};

/** "Varrendo a internet"; fase desconhecida ou sem progresso, "Investigando". */
export function investigationPhaseLabel(phase: string | null | undefined): string {
  return (phase && INVESTIGATION_PHASE_LABELS[phase]) || 'Investigando';
}

/** "12 buscas · 3 páginas lidas" do progresso ao vivo; nulo sem número. */
export function investigationProgressDetail(
  progress: { searches?: number | null; pagesRead?: number | null } | null | undefined
): string | null {
  const parts: string[] = [];
  const searches = progress?.searches ?? 0;
  const pages = progress?.pagesRead ?? 0;
  if (searches > 0) parts.push(searches === 1 ? '1 busca' : `${searches} buscas`);
  if (pages > 0) parts.push(pages === 1 ? '1 página lida' : `${pages} páginas lidas`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Estado da investigação numa versão do briefing (contrato 1.6 do backend), para o aviso do leitor. `none` e estado
 * desconhecido não mostram nada.
 */
export function investigationStateLabel(state: string | null | undefined): string | null {
  switch (state) {
    case 'included':
    case 'adjusted':
      return 'Investigação incluída';
    case 'waiting':
      return 'Investigando a empresa. Quando terminar, os riscos entram no texto sozinhos.';
    case 'adjusting':
      return 'Levando a investigação para o texto.';
    case 'not_completed':
      return 'Investigação não concluída nesta versão.';
    default:
      return null;
  }
}

/** Subseções da Investigação marcadas na seleção (para o "Refazer investigação" pedir as mesmas). */
export function selectedInvestigationSubsections(
  selected: readonly string[],
  catalog: readonly PartnerDealBriefingSectionResponse[],
  parentKey = 'investigation'
): string[] {
  const children = new Set(catalog.filter(s => s.parentKey === parentKey).map(s => s.key));
  return selected.filter(key => children.has(key));
}

/** Altura mínima das colunas da aba: abaixo disso a página rola (palco de ~380px, legível em 1280x680). */
export const BRIEFING_MIN_FILL_HEIGHT = 480;
/** Respiro entre o pé das colunas e o fim da janela. */
export const BRIEFING_BOTTOM_GAP = 16;

/**
 * Altura das colunas da aba (painel Nova versão e leitor) para irem até o fim da janela quando a página está rolada
 * com as abas do deal no topo: janela, menos o menu do topo (`--dashboard-header-offset`), menos a distância das abas
 * ao começo das colunas, menos o respiro. Em 1920x1080 (área útil ~945px, abas a 62px das colunas) dá 867px; em
 * 1366x768 (~650px), 572px; abaixo do mínimo, a página rola.
 */
export function briefingFillHeight(
  viewportHeight: number,
  headerOffset: number,
  offsetFromTabs: number
): number {
  return Math.max(
    BRIEFING_MIN_FILL_HEIGHT,
    Math.round(viewportHeight - headerOffset - offsetFromTabs - BRIEFING_BOTTOM_GAP)
  );
}

/**
 * Largura do chat do Cog IA encostado no modo foco da aba (B2 da crítica da opção B): 440px até 1.600px de janela,
 * 480px acima (protótipo `versao-b-chat`). O leitor fica com o resto e o documento se ajusta à largura.
 */
export function chatDockedWidthFor(viewportWidth: number): number {
  return viewportWidth >= 1600 ? 480 : 440;
}

/** Zoom da prévia (Q11): "ajustar à largura" (padrão, até 1,4x no documento) ou a escala escolhida no controle. */
export type PreviewZoom = 'fit' | number;

/** Passo e limites do controle de zoom da prévia (o documento aceita de 0,3x a 2x no manual). */
export const PREVIEW_ZOOM_STEP = 0.1;
export const PREVIEW_ZOOM_MIN = 0.5;
export const PREVIEW_ZOOM_MAX = 2;

/** Próximo zoom do controle a partir da escala mostrada: − e + em passos de 10%, arredondados, dentro dos limites. */
export function nextPreviewZoom(current: number, direction: 1 | -1): number {
  const stepped = Math.round((current + direction * PREVIEW_ZOOM_STEP) * 10) / 10;
  return Math.min(PREVIEW_ZOOM_MAX, Math.max(PREVIEW_ZOOM_MIN, stepped));
}

/** Linha de versão gerada (ou gerando) no menu "Versão N" (Q11). */
export interface VersionMenuRow {
  id: string;
  number: number;
  createdAt: string;
  requestedBy: string;
  /** Origem e detalhe numa linha ("Ajuste pelo chat: ...", "Formato Resumido · 7 seções"). */
  description: string;
  /** Versão das regras usada (dado interno, só no title). */
  ruleSetVersion: number | null;
  tag: { label: string; tone: 'success' | 'info' | 'danger' } | null;
  isCurrent: boolean;
  isReady: boolean;
}

/** Versão que não foi gerada, no menu: só o motivo numa linha e a data. */
export interface VersionMenuFailedRow {
  id: string;
  number: number;
  createdAt: string;
  reason: string;
}

/**
 * Menu "Versão N" do leitor (Q11, opção B): as geradas (e a que gera) com descrição, as não geradas à parte com o
 * motivo sem jargão, e a contagem do topo ("3 geradas · 2 não geradas"). Versões já ordenadas da mais nova à antiga.
 */
export function versionMenuModel(
  versions: readonly PartnerDealBriefingVersionResponse[],
  catalog: readonly PartnerDealBriefingSectionResponse[]
): { generated: VersionMenuRow[]; failed: VersionMenuFailedRow[]; countLabel: string } {
  const generated = versions
    .filter(version => version.status !== 'Failed')
    .map(version => {
      const summary = versionSummary(version, catalog);
      return {
        id: version.id,
        number: version.versionNumber,
        createdAt: version.completedAt || version.createdAt,
        requestedBy: version.requestedByName?.trim() || 'Cog IA',
        description: `${summary.origin}${summary.separator}${summary.detail}`,
        ruleSetVersion: version.ruleSetVersion ?? null,
        tag: versionTag(version),
        isCurrent: version.isCurrent,
        isReady: version.status === 'Ready',
      };
    });
  const failed = versions
    .filter(version => version.status === 'Failed')
    .map(version => ({
      id: version.id,
      number: version.versionNumber,
      createdAt: version.createdAt,
      reason: failureReasonText(version.failureMessage),
    }));
  const generatedText = generated.length === 1 ? '1 gerada' : `${generated.length} geradas`;
  const countLabel = failed.length
    ? `${generatedText} · ${failed.length === 1 ? '1 não gerada' : `${failed.length} não geradas`}`
    : generatedText;
  return { generated, failed, countLabel };
}
